import React, { useEffect, useState } from "react";
import {
    Box,
    Typography,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Button,
    CircularProgress,
    Chip,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { format } from "date-fns";
import { restaurantService } from "../../services/firebase/restaurantManagementService";
import { FullProperty, PaymentEvent } from "../../models/fullProperty";
import { RefundType } from "../../models/refundType";
import { refundService } from "../../services/firebase/refundService";
import { ManageSpecificRestaurantPage } from "./manageSpecificRestaurant";

const calculateAmountDue = (restaurant: FullProperty) => {
    const netSales = restaurant.totalSalesInZarC - restaurant.totalRefundsInZarC;
    const totalPaid = restaurant.paymentHistory.reduce(
        (sum: number, evt: PaymentEvent) => sum + evt.amount,
        0
    );
    return Math.max(netSales - totalPaid, 0);
};

const getNextPaymentDate = (paymentHistory: PaymentEvent[]): string => {
    if (paymentHistory.length === 0) return "-";
    const lastDate = paymentHistory
        .map((evt) => new Date(evt.paymentDate))
        .sort((a, b) => b.getTime() - a.getTime())[0];
    const nextDue = new Date(lastDate);
    nextDue.setDate(nextDue.getDate() + 30);
    return format(nextDue, "dd MMM yyyy");
};

export const ListRestaurantsPage: React.FC = () => {
    const [restaurants, setRestaurants] = useState<FullProperty[]>([]);
    const [restaurantRefunds, setRestaurantRefunds] = useState<{ [name: string]: RefundType[] }>({});
    const [showManageSpecificRestaurant, setShowManageSpecificRestaurant] = useState<FullProperty | undefined>(undefined);
    const [showManageSpecificRestaurantPendingRefunds, setShowManageSpecificRestaurantPendingRefunds] = useState<RefundType[] | undefined>(undefined);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string>("");
    const navigate = useNavigate();

    useEffect(() => {
        const fetchData = async () => {
            try {
                setLoading(true);
                const data = await restaurantService.fetchAllRestaurants();
                let _restaurantRefunds: { [name: string]: RefundType[] } = {}
                await Promise.all(data.map(async (restaurant) => {
                    _restaurantRefunds[restaurant.id] = await refundService.fetchPendingRefundsByRestaurant(restaurant.id)
                }))
                setRestaurants(data);
                setRestaurantRefunds(_restaurantRefunds)
            } catch (err) {
                setError("Failed to load restaurants. Please try again.");
            } finally {
                setLoading(false);
            }
        };
        fetchData();
    }, []);

    if (loading) {
        return (
            <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
                <CircularProgress />
            </Box>
        );
    }

    if (error) {
        return (
            <Typography color="error" align="center" sx={{ mt: 4 }}>
                {error}
            </Typography>
        );
    }

    if (showManageSpecificRestaurant && showManageSpecificRestaurantPendingRefunds) {
        return <ManageSpecificRestaurantPage
            setRestaurant={setShowManageSpecificRestaurant}
            restaurant={showManageSpecificRestaurant}
            setRestaurantRefunds={setShowManageSpecificRestaurantPendingRefunds}
            refunds={showManageSpecificRestaurantPendingRefunds} />
    }

    console.log("LIST RESTAURANTS PAGE", restaurants)
    return (
        <Box sx={{ p: 3 }} className="patlite">
            <Typography variant="h4" gutterBottom align="center">
                Restaurant Earnings & Payments
            </Typography>
            <TableContainer component={Paper} sx={{ mt: 2 }}>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell>Restaurant</TableCell>
                            <TableCell align="center">Plant Based Treaty</TableCell>
                            <TableCell align="right">Status</TableCell>
                            <TableCell align="right">Published</TableCell>
                            <TableCell align="right">Total Sales</TableCell>
                            <TableCell align="right">Total Refunds</TableCell>
                            <TableCell align="right">Net Earned</TableCell>
                            <TableCell align="right">Amount Due</TableCell>
                            <TableCell align="right">Next Payment Due</TableCell>
                            <TableCell align="center">Pending Refunds</TableCell>
                            <TableCell align="center">Action</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {restaurants.map((rest) => {
                            const netSales = (rest.totalSalesInZarC - rest.totalRefundsInZarC) / 100;
                            const amountDue = calculateAmountDue(rest) / 100;
                            const dueDate = getNextPaymentDate(rest.paymentHistory);
                            return (
                                <TableRow key={rest.id} hover>
                                    <TableCell>{rest.name}</TableCell>
                                    <TableCell align="right">
                                        <Chip
                                            label={rest.isPlantBasedTreaty ? "Yes" : "No"}
                                            size="small"
                                            color={rest.isPlantBasedTreaty ? "success" : "default"}
                                        />
                                    </TableCell>
                                    <TableCell align="right">
                                        <Chip
                                            label={rest.isActive ? "Active" : "Inactive"}
                                            size="small"
                                            color={rest.isActive ? "success" : "default"}
                                        />
                                    </TableCell>
                                    <TableCell align="right">
                                        <Chip
                                            label={rest.isPublished ? "Published" : "Draft"}
                                            size="small"
                                            color={rest.isPublished ? "success" : "default"}
                                        />
                                    </TableCell>
                                    <TableCell align="right">£{(rest.totalSalesInZarC / 100).toFixed(2)}</TableCell>
                                    <TableCell align="right">£{(rest.totalRefundsInZarC / 100).toFixed(2)}</TableCell>
                                    <TableCell align="right">£{netSales.toFixed(2)}</TableCell>
                                    <TableCell align="right">£{amountDue.toFixed(2)}</TableCell>
                                    <TableCell align="right">{dueDate}</TableCell>
                                    <TableCell align="right">{restaurantRefunds[rest.id].length}</TableCell>
                                    <TableCell align="center">
                                        <Button
                                            variant="contained"
                                            size="small"
                                            onClick={() => {
                                                setShowManageSpecificRestaurantPendingRefunds(restaurantRefunds[rest.id])
                                                setShowManageSpecificRestaurant(rest)
                                            }}
                                        >
                                            Manage
                                        </Button>
                                    </TableCell>
                                </TableRow>
                            );
                        })}
                    </TableBody>
                </Table>
            </TableContainer>
        </Box>
    );
};
